import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import numbro from 'numbro';

import { SmartParentsProgramTemplate } from '../../components/Templates';
import { LoadingSpinner } from '../../components/Molecules';

import { useShareData } from '../../context';
import { smartParentPlanPrices } from '../../constants/exchangeRates';
import { DEFAULT_TRY_CLASSROOMID } from '../../constants/appInfo';
import { RegionsCode } from '../../constants/region';
import useDoWhile, { DoWhileFn } from '../../hooks/useDoWhile';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useUserStore } from '../../stores/UserStore';
import {
  ReferralSystem,
  RewardRedemption,
  RewardRedemptionState,
  RewardRedemptionTabState,
} from '../../models/RewardStore';
import { ResetPasswordResult } from '../../models/UserStore';
import { AlbumListType, SpaceListType } from '../../models/SpaceStore';
import { arrayEqual } from '../../utils/array';
import { getLocalCurrencyById, isValidRegionCode } from '../../utils/region';

interface State {
  referralCode: string;
  lastReferralCode: string;
  disabled: boolean;
  rewardRedemptionId: string | undefined;
  isLoading: boolean;
  errorMessage: string;
  isFocused: boolean;
  validationLoading: boolean;
  isValid: boolean;
  hasCancelled: boolean;
  isFirstChecked: boolean;
  isNeedFetch: boolean;
}

const {
  fetchRedemption,
  createRedemption,
  cancelRedemption,
  referralCodeValidation,
  fetchRewardReferral,
} = useRewardStore.getState();
const { fetchSpace } = useSpaceStore.getState();

const SmartParentsProgramScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSharedData } = useShareData();
  const userId = useSessionStore((store) => store.activeUserId);
  const activeRegion = useSessionStore((store) => store.activeRegion);

  const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));
  const userProfile = useUserStore((state) => state.getProfile(userId ?? ''));

  const { region: paramRegion } = useParams();

  useEffect(() => {
    if (paramRegion && isValidRegionCode(paramRegion)) {
      useSessionStore.getState().setActiveRegion(paramRegion as RegionsCode);
    }
  }, [paramRegion]);

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? getLocalCurrencyById(activeRegion);
  }, [activeRegion, userProfile?.localCurrency]);

  useEffect(() => {
    setSharedData({
      pathName: location.pathname,
    });
  }, [location.pathname, setSharedData]);

  useEffect(() => {
    if (userId) {
      fetchRewardReferral(
        { referralSystem: ReferralSystem.SmartParentsPlan },
        { headers: { 'Cache-Control': 'no-cache' } },
      ).catch(console.error);
    }
  }, [userId]);

  const query = new URLSearchParams(location.search);

  const redemptionId = query.get('rewardRedemptionId');
  const referralCode = query.get('referralCode');

  const rewardReferral = useRewardStore(
    (store) =>
      store.getRewardReferral(
        `${ReferralSystem.SmartParentsPlan}_${userId}` ?? '',
      ),
    shallow,
  );

  const fieldName = `system_${RewardRedemptionTabState.Paying}`;
  const collectionField = useRewardStoreListField('redemptions', fieldName);

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchRedemptions('system', RewardRedemptionTabState.Paying, cursor),
    [],
  );

  const { fetchAll: fetchAllRewardRedemptions } = useFetchList(
    collectionField,
    fetchRewardRedemptions,
  );

  const [state, setState] = useState<State>({
    referralCode:
      rewardReferral?.rewardReferralCode?.referralCode || referralCode || '',
    lastReferralCode: '',
    disabled: rewardReferral?.rewardReferralCode?.referralCode ? true : false,
    rewardRedemptionId: undefined,
    isLoading: false,
    errorMessage: '',
    isFocused: false,
    validationLoading: false,
    isValid: false,
    hasCancelled: false,
    isFirstChecked: false,
    isNeedFetch: false,
  });

  useEffect(() => {
    if (
      (redemptionId === null && userId && !state.hasCancelled) ||
      state.isNeedFetch
    ) {
      fetchAllRewardRedemptions().catch(console.error);
    }
  }, [
    fetchAllRewardRedemptions,
    redemptionId,
    userId,
    state.hasCancelled,
    state.isNeedFetch,
  ]);

  const redemptions = useRewardStore((rewardState) => {
    return rewardState
      .getRedemptions(fieldName)
      .reduce<Pick<RewardRedemption, 'rewardRedemptionId' | 'rewardOfferId'>[]>(
        (acc, offer) => {
          if (offer.state === RewardRedemptionState.Paying) {
            acc.push({
              rewardRedemptionId: offer.rewardRedemptionId,
              rewardOfferId: offer.rewardOfferId,
            });
          }
          return acc;
        },
        [],
      );
  }, arrayEqual(shallow));

  const payingRedemption = useMemo(() => {
    return redemptions.find((item) => item);
  }, [redemptions]);

  const checkReferralCode = useCallback(async () => {
    if (state.referralCode === state.lastReferralCode && state.isFirstChecked) {
      return;
    }
    if (
      state.referralCode.trim().length > 0 &&
      state.referralCode === userAccount?.userCode
    ) {
      setState((s) => ({
        ...s,
        isValid: false,
        errorMessage: t('Invalid Code. This is your referral code.'),
      }));
      return;
    }
    if (state.referralCode) {
      setSharedData({
        pathName: location.pathname,
        referralCode: state.referralCode,
      });
      setState((s) => ({ ...s, validationLoading: true }));
      const res = await referralCodeValidation(
        { referralSystem: ReferralSystem.SmartParentsPlan },
        { referralCode: state.referralCode },
      );
      setState((s) => ({
        ...s,
        validationLoading: false,
        isFirstChecked: true,
        lastReferralCode: state.referralCode,
      }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({
          ...s,
          isValid: true,
          disabled: true,
          errorMessage: '',
        }));
      } else {
        setState((s) => ({
          ...s,
          isValid: false,
          errorMessage: t('Please check your referral code and try again.'),
        }));
      }
    } else {
      setState((s) => ({ ...s, isValid: false, errorMessage: '' }));
    }
  }, [
    location.pathname,
    setSharedData,
    state.isFirstChecked,
    state.lastReferralCode,
    state.referralCode,
    t,
    userAccount?.userCode,
  ]);

  useEffect(() => {
    const code = rewardReferral?.rewardReferralCode?.referralCode;
    if (code) {
      setState((s) => ({
        ...s,
        disabled: true,
        referralCode: code,
      }));
    }
  }, [rewardReferral?.rewardReferralCode?.referralCode]);

  useEffect(() => {
    if (state.disabled || referralCode || !state.isFirstChecked) {
      checkReferralCode().catch(() => {});
    }
  }, [checkReferralCode, referralCode, state.disabled, state.isFirstChecked]);

  useEffect(() => {
    if (
      (redemptionId !== null ||
        payingRedemption?.rewardRedemptionId !== undefined) &&
      !state.hasCancelled
    ) {
      const idToCancel: string =
        redemptionId || payingRedemption?.rewardRedemptionId || '';

      cancelRedemption('system', idToCancel)
        .then(() => {
          setState((s) => ({ ...s, hasCancelled: true }));
        })
        .catch(() => {
          setState((s) => ({ ...s, hasCancelled: true }));
        });
    }
  }, [
    state.hasCancelled,
    navigate,
    payingRedemption?.rewardRedemptionId,
    redemptionId,
    state.referralCode,
  ]);

  const loadRedemption = useDoWhile(
    useCallback<DoWhileFn>(
      async (signal) => {
        if (state.rewardRedemptionId !== undefined) {
          const res = await fetchRedemption(
            'system',
            state.rewardRedemptionId,
            { signal },
          );
          if (res) {
            switch (res.state) {
              case RewardRedemptionState.Paying:
                {
                  const url = res.payment?.url;
                  if (url !== undefined) {
                    setState((s) => ({ ...s, isLoading: false }));
                    window.location.href = url;
                  }
                }
                return true;
              case RewardRedemptionState.Fulfilled:
                // navigate(
                //   `/smart-parents/rewardRedemption/${state.rewardRedemptionId}?success=true`,
                // );
                return true;
              case RewardRedemptionState.Failed:
                // navigate(
                //   `/smart-parents/rewardRedemption/${state.rewardRedemptionId}?success=false`,
                // );
                return false;
            }
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return false;
      },
      [state.rewardRedemptionId],
    ),
  );

  useEffect(() => {
    if (state.rewardRedemptionId !== undefined) {
      loadRedemption().catch(() => {});
    }
  }, [loadRedemption, redemptionId, state.rewardRedemptionId]);

  const onClickViewDetails = useCallback(() => {
    window.open('https://joyajoy.com/pages/smart-parents-program', '_blank');
  }, []);

  const onClickViewGurantee = useCallback(() => {
    navigate('/smart-parents/guaranteed-detail');
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
  }, [navigate]);
  const onClickViewReward = useCallback(() => {
    navigate('/smart-parents/referral-detail');
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
  }, [navigate]);

  const onClickPurchase = useCallback(() => {
    if (!userId) {
      navigate('/start');
    } else {
      setState((s) => ({ ...s, isLoading: true }));
      createRedemption('system', {
        rewardOfferId: 'smart-parents-plan',
        quantity: 1,
        paymentGateway: 'stripe',
        referralCode: state.referralCode,
      })
        .then((res) => {
          if (res) {
            setState((s) => ({
              ...s,
              rewardRedemptionId: res.rewardRedemptionId,
            }));
          } else {
            setState((s) => ({ ...s, isLoading: false }));
            toast.error(
              t('We are cancelling the previous order... Please try again.'),
            );
            setState((s) => ({ ...s, isNeedFetch: true }));
          }
        })
        .catch(() => {
          setState((s) => ({ ...s, isLoading: false }));
          toast.error(
            t('We are cancelling the previous order... Please try again.'),
          );
          setState((s) => ({ ...s, isNeedFetch: true }));
        });
    }
  }, [userId, navigate, state.referralCode, t]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        const url = new URL(window.location.href);
        if (url.search) {
          url.search = '';
          window.history.replaceState(null, '', url.toString());
          window.location.reload(); // Reload the page when navigating back
        } else {
          window.location.reload(); // Reload the page when navigating back
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // THIS IS SOME TESTING METHOD ON NAVIGATION METHOD
  // window.addEventListener('pageshow', () => {
  //   console.log('Returned to Page A pageshow from an external page');
  // });

  // useEffect(() => {
  //   const handlePageShow = () => {
  //     console.log(
  //       'Returned to Page A use Effect hashchange from an external page',
  //     );
  //   };

  //   window.addEventListener('hashchange', handlePageShow);

  //   return () => {
  //     window.removeEventListener('hashchange', handlePageShow);
  //   };
  // }, []);

  // window.addEventListener('hashchange', () => {
  //   console.log('Returned to Page A hashChange from an external page');
  // });

  // useEffect(() => {
  //   const handlePageShow = () => {
  //     console.log(
  //       'Returned to Page A use Effect popstate from an external page',
  //     );
  //   };

  //   window.addEventListener('popstate', handlePageShow);

  //   return () => {
  //     window.removeEventListener('popstate', handlePageShow);
  //   };
  // }, []);

  // window.addEventListener('popstate', () => {
  //   console.log('Returned to Page A popstate from an external page');
  // });

  // interface CustomPerformanceEntry extends PerformanceNavigationTiming {
  //   type: NavigationTimingType;
  // }

  // if (window.performance) {
  //   const navEntries = window.performance.getEntriesByType(
  //     'navigation',
  //   ) as CustomPerformanceEntry[];
  //   if (navEntries.length > 0 && navEntries[0]?.type === 'back_forward') {
  //     console.log('As per API lv2, this page is load from back/forward');
  //   } else if (
  //     window.performance.navigation &&
  //     window.performance.navigation.type ==
  //       window.performance.navigation.TYPE_BACK_FORWARD
  //   ) {
  //     console.log('As per API lv1, this page is load from back/forward');
  //   } else {
  //     console.log('This is normal page load');
  //   }
  // } else {
  //   console.log("Unfortunately, your browser doesn't support this API");
  // }

  // useEffect(() => {
  //   if (window.performance) {
  //     const navEntries = window.performance.getEntriesByType(
  //       'navigation',
  //     ) as CustomPerformanceEntry[];
  //     if (navEntries.length > 0 && navEntries[0]?.type === 'back_forward') {
  //       console.log(
  //         'UseEffect As per API lv2, this page is loaded from back/forward',
  //       );
  //     } else if (
  //       window.performance.navigation &&
  //       window.performance.navigation.type ===
  //         window.performance.navigation.TYPE_BACK_FORWARD
  //     ) {
  //       console.log(
  //         'UseEffect As per API lv1, this page is loaded from back/forward',
  //       );
  //     } else {
  //       console.log('UseEffect This is a normal page load');
  //     }
  //   } else {
  //     console.log(
  //       "UseEffect Unfortunately, your browser doesn't support this API",
  //     );
  //   }
  // }, []);

  const onReferralCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, referralCode: event.target.value }));
    },
    [],
  );

  const handleFocus = () => {
    setState((s) => ({ ...s, isFocused: true }));
  };

  const handleBlur = () => {
    setState((s) => ({ ...s, isFocused: false }));
    checkReferralCode().catch(() => {});
  };

  const classroomId = DEFAULT_TRY_CLASSROOMID;

  useSpaceStoreListField('spaces', SpaceListType.Recommended);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(SpaceListType.Recommended, classroomId).catch(console.error);
    }
  }, [classroomId]);

  const tryClassroom = useSpaceStore((store) => {
    return store.getSpace(classroomId);
  }, shallow);

  const albumNormalField = useSpaceStoreListField(
    'albums',
    AlbumListType.TryAlbum,
  );

  const fetchAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchAlbums(
        {
          type: AlbumListType.TryAlbum,
          classroomId: classroomId,
        },
        cursor,
      ),
    [classroomId],
  );

  const { refresh: refreshAlbums } = useFetchList(
    albumNormalField,
    fetchAlbums,
  );

  useEffect(() => {
    if (classroomId && userId) {
      refreshAlbums().catch(console.error);
    }
  }, [refreshAlbums, classroomId, userId]);

  const onClickAlbum = useCallback(() => {
    navigate(`/club/${classroomId}/try`);
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
  }, [classroomId, navigate]);

  const albums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(AlbumListType.TryAlbum);
  }, arrayEqual(shallow));

  const albumCellProps = useMemo(() => {
    const album = albums[0];
    if (album && tryClassroom) {
      return {
        album: album,
        albumClassroom: tryClassroom,
        onClick: () => {
          onClickAlbum();
        },
      };
    }
    return undefined;
  }, [albums, onClickAlbum, tryClassroom]);

  return (
    <>
      <SmartParentsProgramTemplate
        originalYearCurrency={`${currency}${numbro(
          smartParentPlanPrices[currency].originalPrice,
        ).format({ thousandSeparated: true })}`}
        originalMonthCurrency={`${currency}${numbro(
          smartParentPlanPrices[currency].originalPrice / 12,
        ).format({ thousandSeparated: true, mantissa: 2 })}`}
        referralYearCurrency={
          state.isValid
            ? `${currency}${numbro(
                smartParentPlanPrices[currency].referralPrice,
              ).format({ thousandSeparated: true })}`
            : `${currency}${numbro(
                smartParentPlanPrices[currency].specialPrice,
              ).format({ thousandSeparated: true })}`
        }
        referralMonthCurrency={
          state.isValid
            ? `${currency}${numbro(
                smartParentPlanPrices[currency].referralPrice / 12,
              ).format({ thousandSeparated: true, mantissa: 2 })}`
            : `${currency}${numbro(
                smartParentPlanPrices[currency].specialPrice / 12,
              ).format({ thousandSeparated: true, mantissa: 2 })}`
        }
        tier1Currency={`${currency}${numbro(
          smartParentPlanPrices[currency].tier1PurchaseReward,
        ).format({ thousandSeparated: true, mantissa: 2 })}`}
        tier2Currency={`${currency}${numbro(
          smartParentPlanPrices[currency].tier2PurchaseReward,
        ).format({ thousandSeparated: true, mantissa: 2 })}`}
        referralCode={{
          value: state.referralCode,
          onChange: onReferralCodeChange,
          disabled: state.disabled,
          errorMessage: state.errorMessage,
          onFocus: handleFocus,
          onBlur: handleBlur,
        }}
        onClickViewDetails={onClickViewDetails}
        onClickPurchase={onClickPurchase}
        onClickViewReward={onClickViewReward}
        onClickViewGurantee={onClickViewGurantee}
        validationLoading={state.validationLoading}
        purchasedPlan={userAccount?.hasActiveSmartParentPlan ?? false}
        albumCellProps={albumCellProps}
        currency={currency}
        isValid={state.isValid}
      />
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </>
  );
};

export default SmartParentsProgramScreen;
